import { CarrierBillingAccountType } from "@redotech/congo-enums/carrier-billing-account-type";
import { OutboundPricingProgram } from "@redotech/congo-enums/outbound-pricing-program";
import { BreakdownDisplayMode } from "@redotech/crdb-enums/xborder-outbound/breakdown-display-mode";
import { StruckSnapshotSchema } from "@redotech/money";
import { Currency } from "@redotech/money/currencies";
import { CanadaPostManifestMode } from "@redotech/redo-fulfillment-carrier-account-common/canada-post";
import {
  billingAccountType,
  CarrierBillingAccount,
} from "@redotech/redo-fulfillment-carrier-account-common/carrier-billing-accounts";
import { ServiceSchema } from "@redotech/redo-fulfillment-carrier-common/carrier-service-catalog";
import {
  Carriers,
  FedexServices,
} from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import {
  APO_ENABLED_CARRIERS,
  CarrierSchema,
  isFedexCarrier,
  PO_BOX_ENABLED_CARRIERS,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import { HazmatSchema } from "@redotech/redo-fulfillment-carrier-common/hazmat";
import {
  ContentsType,
  NonDeliveryOption,
  OutboundShipmentTaxIdSchema,
  USPSExemptionCode,
} from "@redotech/redo-fulfillment-customs-common/customs";
import { CustomsDeclarationSchema } from "@redotech/redo-fulfillment-customs-common/customs-declaration";
import {
  FulfillmentOrderAddressSchema,
  VerifiedAddressSchema,
} from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { rateLandedCostSchema } from "@redotech/redo-fulfillment-customs-common/rate-landed-cost";
import {
  MoneySchema,
  WeightSchema,
} from "@redotech/redo-fulfillment-outbound-labels-common/util";
import { OutboundShipmentParcelV2Schema } from "@redotech/redo-fulfillment-parcel-common/outbound-shipment-parcel-v2";
import { OutboundParcelSchema } from "@redotech/redo-fulfillment-parcel-common/parcel";
import { privacy } from "@redotech/schema-privacy";
import { isEnumValue } from "@redotech/util/enum";
import { assertNever } from "@redotech/util/type";
import { DateFromDateOrIsoStringSchema } from "@redotech/zod-util/date-schema";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

// Some info on payment responsibilities: https://www.ups.com/assets/resources/webcontent/en_GB/UPS_Payment_Options.pdf
export enum PaymentResponsibility {
  TRANSPORTATION = "TRANSPORTATION",
  DUTIES_AND_TAXES = "DUTIES_AND_TAXES",
  VAT = "VAT",
}

export enum ShipmentPaymentOptionType {
  SENDER = "SENDER",
  THIRD_PARTY = "THIRD_PARTY",
  RECEIVER = "RECEIVER",
  COLLECT = "COLLECT",
}

export enum UspsBillingType {
  REDO = "redo",
  MERCHANT = "merchant",
}

const BasePaymentOptionSchema = z
  .object({
    explicit_payment_responsibilities: z
      .array(
        createEnum(PaymentResponsibility, {
          enumName: "PaymentResponsibility",
        }).meta({ privacy: privacy.nonPii }),
      )
      .nullish()
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });

const SenderPaymentOptionSchema = BasePaymentOptionSchema.extend({
  type: z
    .literal(ShipmentPaymentOptionType.SENDER)
    .meta({ privacy: privacy.nonPii }),
});

const ThirdPartyPaymentOptionSchema = BasePaymentOptionSchema.extend({
  type: z
    .literal(ShipmentPaymentOptionType.THIRD_PARTY)
    .meta({ privacy: privacy.nonPii }),
  carrier: createEnum(Carriers, { enumName: "Carriers" }).meta({
    privacy: privacy.nonPii,
  }), // Not part of the easypost schema, but needed to determine which rates to return
  account: z.string().meta({ privacy: privacy.credential }),
  country: z.string().meta({ privacy: privacy.geo.coarse }),
  postal_code: z.string().meta({ privacy: privacy.geo.coarse }),
  thirdPartyBillingAccountId: z
    .string()
    .meta({ privacy: privacy.id.internal })
    .nullish(),
});

export type ThirdPartyPaymentOption = z.infer<
  typeof ThirdPartyPaymentOptionSchema
>;

const ReceiverPaymentOptionSchema = BasePaymentOptionSchema.extend({
  type: z
    .literal(ShipmentPaymentOptionType.RECEIVER)
    .meta({ privacy: privacy.nonPii }),
  carrier: createEnum(Carriers, { enumName: "Carriers" }).meta({
    privacy: privacy.nonPii,
  }), // Not part of the easypost schema, but needed to determine which rates to return
  account: z.string().meta({ privacy: privacy.credential }),
  /** UPS Bill Receiver takes no country code, but FedEx and EasyPost accept one. */
  country: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
  postal_code: z.string().meta({ privacy: privacy.geo.coarse }),
  receiverBillingAccountId: z
    .string()
    .meta({ privacy: privacy.id.internal })
    .nullish(),
});

export type ReceiverPaymentOption = z.infer<typeof ReceiverPaymentOptionSchema>;

const CollectPaymentOptionSchema = BasePaymentOptionSchema.extend({
  type: z
    .literal(ShipmentPaymentOptionType.COLLECT)
    .meta({ privacy: privacy.nonPii }),
});

export const ShipmentPaymentOptionSchema = z
  .discriminatedUnion("type", [
    SenderPaymentOptionSchema.meta({ privacy: privacy.structured }),
    ThirdPartyPaymentOptionSchema.meta({ privacy: privacy.structured }),
    ReceiverPaymentOptionSchema.meta({ privacy: privacy.structured }),
    CollectPaymentOptionSchema.meta({ privacy: privacy.structured }),
  ])
  .meta({ privacy: privacy.structured });

export function isPaymentSplitDutyVat(
  paymentOption: ShipmentPaymentOption | null | undefined,
): boolean {
  if (!paymentOption) {
    return false;
  }
  return (
    paymentOption.type === ShipmentPaymentOptionType.THIRD_PARTY &&
    paymentOption.explicit_payment_responsibilities?.length === 1 &&
    paymentOption.explicit_payment_responsibilities?.[0] ===
      PaymentResponsibility.VAT
  );
}

export function isThirdPartyOnlyPayingVat(
  paymentOption: ShipmentPaymentOption | null | undefined,
): boolean {
  if (!paymentOption) {
    return false;
  }
  return !!(
    paymentOption.type === ShipmentPaymentOptionType.THIRD_PARTY &&
    paymentOption.explicit_payment_responsibilities?.includes(
      PaymentResponsibility.VAT,
    )
  );
}

/**
 * True when the carrier bills an account we don't own — a third party's or the
 * receiver's. Redo neither pays for nor sees the real price of these labels.
 */
export function isExternalAccountBilling(
  paymentOption: ShipmentPaymentOption | null | undefined,
): paymentOption is ThirdPartyPaymentOption | ReceiverPaymentOption {
  return (
    paymentOption?.type === ShipmentPaymentOptionType.THIRD_PARTY ||
    paymentOption?.type === ShipmentPaymentOptionType.RECEIVER
  );
}

export function getBillingAccountId(
  paymentOption: ShipmentPaymentOption | null | undefined,
): string | undefined {
  // tslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: grandfather FIXME
  switch (paymentOption?.type) {
    case ShipmentPaymentOptionType.THIRD_PARTY: {
      return paymentOption.thirdPartyBillingAccountId ?? undefined;
    }
    case ShipmentPaymentOptionType.RECEIVER: {
      return paymentOption.receiverBillingAccountId ?? undefined;
    }
    default: {
      return undefined;
    }
  }
}

/** Builds the payment option a stored billing account represents. */
export function paymentOptionForBillingAccount(
  account: Pick<
    CarrierBillingAccount,
    "_id" | "carrier" | "accountNumber" | "country" | "postalCode"
  > & { billingType?: CarrierBillingAccountType | null },
  explicitPaymentResponsibilities: PaymentResponsibility[] = [],
): ThirdPartyPaymentOption | ReceiverPaymentOption {
  const shared = {
    carrier: account.carrier,
    account: account.accountNumber,
    country: account.country,
    postal_code: account.postalCode,
    explicit_payment_responsibilities: explicitPaymentResponsibilities,
  };
  if (billingAccountType(account) === CarrierBillingAccountType.Receiver) {
    return {
      ...shared,
      type: ShipmentPaymentOptionType.RECEIVER,
      receiverBillingAccountId: account._id,
    };
  }
  return {
    ...shared,
    type: ShipmentPaymentOptionType.THIRD_PARTY,
    thirdPartyBillingAccountId: account._id,
  };
}

export type ShipmentPaymentOption = z.infer<typeof ShipmentPaymentOptionSchema>;
export interface IShipmentPaymentOption extends z.ZodType<ShipmentPaymentOption> {}

export enum ShipmentRefundStatus {
  Submitted = "submitted",
  Refunded = "refunded",
  Rejected = "rejected",
  NotApplicable = "not_applicable",
  AlreadyShipped = "already_shipped", // A redo status
}

export const IB_CARRIER_ACCOUNT_ID = "IB";

export enum RateProvider {
  SOAP_BOX = "SOAP_BOX",
  EASYPOST = "EASYPOST",
  UPS = "UPS",
  LANDMARK_GLOBAL = "LANDMARK_GLOBAL",
  REDO_LANDMARK_GLOBAL = "REDO_LANDMARK_GLOBAL",
  INTERNATIONAL_BRIDGE = "INTERNATIONAL_BRIDGE",
  ETA_EXPRESS = "ETA_EXPRESS",
  TEST_CARRIER = "TEST_CARRIER",
  FEDEX = "FEDEX",
  USPS = "USPS",
  DHL_ECOMMERCE = "DHL_ECOMMERCE",
  GLOBALE = "GLOBALE",
  TIKTOK = "TIKTOK",
  CANADA_POST = "CanadaPost",
  UNIUNI = "UNIUNI",
  SENDCLOUD = "SENDCLOUD",
  STALLION_EXPRESS = "STALLION_EXPRESS",
  ECOM_EXPRESS = "ECOM_EXPRESS",
  AMAZON_BUY_SHIPPING = "AMAZON_BUY_SHIPPING",
  AMAZON_SHIPPING = "AMAZON_SHIPPING",
  CHITCHATS = "CHITCHATS",
  FREIGHTWIRE = "FREIGHTWIRE",
  WEGROW = "WEGROW",
}

export const rateTableProviderLabel: Record<RateProvider, string> = {
  [RateProvider.SOAP_BOX]: "Soap Box",
  [RateProvider.EASYPOST]: "EasyPost",
  [RateProvider.UPS]: "UPS",
  [RateProvider.LANDMARK_GLOBAL]: "Landmark Global",
  [RateProvider.REDO_LANDMARK_GLOBAL]: "Redo Landmark Global",
  [RateProvider.INTERNATIONAL_BRIDGE]: "International Bridge",
  [RateProvider.ETA_EXPRESS]: "ETA Express",
  [RateProvider.TEST_CARRIER]: "Test Carrier",
  [RateProvider.FEDEX]: "FedEx",
  [RateProvider.USPS]: "USPS",
  [RateProvider.DHL_ECOMMERCE]: "DHL eCommerce",
  [RateProvider.CANADA_POST]: "Canada Post",
  [RateProvider.GLOBALE]: "Global-E",
  [RateProvider.TIKTOK]: "TikTok",
  [RateProvider.UNIUNI]: "UniUni",
  [RateProvider.SENDCLOUD]: "Sendcloud",
  [RateProvider.STALLION_EXPRESS]: "Stallion Express",
  [RateProvider.ECOM_EXPRESS]: "Ecom Express",
  [RateProvider.AMAZON_BUY_SHIPPING]: "Amazon Buy Shipping",
  [RateProvider.AMAZON_SHIPPING]: "Amazon Shipping",
  [RateProvider.CHITCHATS]: "Chit Chats",
  [RateProvider.FREIGHTWIRE]: "FreightWire",
  [RateProvider.WEGROW]: "WeGrow",
};

export const ShipmentMessageSchema = z
  .object({
    carrier: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    carrier_account_id: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    message: z.string().meta({ privacy: privacy.systemErrorText }).nullish(),
    type: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    ignored: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
  })
  .meta({ privacy: privacy.structured });
export type ShipmentMessage = z.infer<typeof ShipmentMessageSchema>;

const AmazonBuyShippingRequestedDocumentSpecificationSchema_ = z
  .object({
    format: z.enum(["PDF", "PNG", "ZPL"]),
    size: z.object({
      width: z.number().meta({ privacy: privacy.nonPii }),
      length: z.number().meta({ privacy: privacy.nonPii }),
      unit: z.enum(["INCH", "CENTIMETER"]),
    }),
    dpi: z.number().meta({ privacy: privacy.nonPii }).int().nullish(),
    pageLayout: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    needFileJoining: z.boolean().meta({ privacy: privacy.nonPii }),
    requestedDocumentTypes: z.array(
      z.enum(["PACKSLIP", "LABEL", "RECEIPT", "CUSTOM_FORM"]),
    ),
    requestedLabelCustomization: z
      .object({
        requestAttributes: z
          .array(
            z.enum([
              "PACKAGE_CLIENT_REFERENCE_ID",
              "SELLER_DISPLAY_NAME",
              "COLLECT_ON_DELIVERY_AMOUNT",
            ]),
          )
          .nullish(),
      })
      .nullish(),
  })
  .passthrough();
type AmazonBuyShippingRequestedDocumentSpecification = z.infer<
  typeof AmazonBuyShippingRequestedDocumentSpecificationSchema_
>;
const AmazonBuyShippingRequestedDocumentSpecificationSchema = z
  .custom<AmazonBuyShippingRequestedDocumentSpecification>(
    (value) =>
      AmazonBuyShippingRequestedDocumentSpecificationSchema_.safeParse(value)
        .success,
  )
  .meta({ annotation: "JSON" });

const ProviderInfoSchema_ = z
  .object({
    soapBox: z
      .object({
        orderId: z.string().meta({ privacy: privacy.nonPii }),
        packageId: z.string().meta({ privacy: privacy.nonPii }),
        externalOrderNumber: z
          .string()
          .meta({ privacy: privacy.nonPii })
          .nullish(),
        batchId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    ups: z
      .object({
        shipperId: z.string().meta({ privacy: privacy.nonPii }),
        // Present when this rate is a UPS Simple Rate (flat rate); `code` is the
        // rated size (XS–XL) and is replayed when buying the label.
        simpleRate: z
          .object({
            code: z.string().meta({ privacy: privacy.nonPii }),
            standardProviderCost: z
              .string()
              .meta({ privacy: privacy.nonPii })
              .nullish(),
            providerCostSavings: z
              .string()
              .meta({ privacy: privacy.nonPii })
              .nullish(),
          })
          .nullish()
          .meta({ privacy: privacy.structured }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    landmarkGlobal: z
      .object({
        username: z.string().meta({ privacy: privacy.username.merchant }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    freightWire: z
      .object({
        bookNumber: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        quoteId: z.number().meta({ privacy: privacy.nonPii }).nullish(),
        carrierCode: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        serviceCode: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        packageTypeCode: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    easypost: z
      .object({
        childAccountId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        orderId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    usps: z
      .object({
        rateIndicator: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        processingCategory: z
          .string()
          .meta({ privacy: privacy.nonPii })
          .nullish(),
        destinationEntryFacilityType: z
          .string()
          .meta({ privacy: privacy.nonPii })
          .nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    fedex: z
      .object({
        accountNumber: z.string().meta({ privacy: privacy.credential }),
        packagingType: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    dhlEcommerce: z
      .object({
        pickupId: z.string().meta({ privacy: privacy.nonPii }),
        distributionCenter: z.string().meta({ privacy: privacy.nonPii }),
        redoPackageId: z.string().meta({ privacy: privacy.nonPii }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    globale: z
      .object({
        globalEOrderId: z.string().meta({ privacy: privacy.nonPii }),
        lineItems: z
          .array(
            z
              .object({
                sku: z.string().meta({ privacy: privacy.nonPii }),
                quantity: z.number().meta({ privacy: privacy.nonPii }),
              })
              .meta({ privacy: privacy.structured }),
          )
          .meta({ privacy: privacy.structured }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    tiktok: z
      .object({
        shippingServiceId: z.string().meta({ privacy: privacy.nonPii }),
        tiktokShopId: z.string().meta({ privacy: privacy.nonPii }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    uniUni: z
      .object({
        pickupWarehouseId: z.number().meta({ privacy: privacy.nonPii }),
        customerId: z.string().meta({ privacy: privacy.nonPii }),
        // UniUni's tracking number, returned by POST /orders/createbusinessorder.
        // Primary key for tracking lookups, POD fetches, and cancellations.
        tno: z.string().meta({ privacy: privacy.id.tracking }).nullish(),
        // UniUni's human-readable order serial number. Secondary identifier
        // returned alongside `tno`; kept for traceability against UniUni records.
        orderSn: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    stallionExpress: z
      .object({
        postageType: z.string().meta({ privacy: privacy.nonPii }),
        shipCode: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    ecomExpress: z
      .object({
        serviceLevelCode: z.string().meta({ privacy: privacy.nonPii }),
        shipmentCode: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        shipmentReferenceId: z
          .string()
          .meta({ privacy: privacy.nonPii })
          .nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    amazonBuyShipping: z
      .object({
        requestToken: z.string().meta({ privacy: privacy.credential }),
        rateId: z.string().meta({ privacy: privacy.nonPii }),
        carrierId: z.string().meta({ privacy: privacy.nonPii }),
        amazonIntegrationId: z.string().meta({ privacy: privacy.nonPii }),
        amazonIntegrationTeamId: z.string().meta({ privacy: privacy.nonPii }),
        shippingBusinessId: z.string().meta({ privacy: privacy.nonPii }),
        requestedDocumentSpecification:
          AmazonBuyShippingRequestedDocumentSpecificationSchema.meta({
            privacy: privacy.untypedPayload,
          }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    amazonShipping: z
      .object({
        carrierAccountId: z.string().meta({ privacy: privacy.nonPii }),
        teamId: z.string().meta({ privacy: privacy.nonPii }),
        aliasOfCarrierAccountId: z
          .string()
          .meta({ privacy: privacy.nonPii })
          .nullish(),
        requestToken: z.string().meta({ privacy: privacy.credential }),
        rateId: z.string().meta({ privacy: privacy.nonPii }),
        /** Amazon's service id; a stale rate buys it through oneClickShipment. */
        serviceId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        carrierId: z.string().meta({ privacy: privacy.nonPii }),
        shippingBusinessId: z.string().meta({ privacy: privacy.nonPii }),
        requestedDocumentSpecification:
          AmazonBuyShippingRequestedDocumentSpecificationSchema.meta({
            privacy: privacy.untypedPayload,
          }),
        requestedValueAddedServices: z
          .array(
            z
              .object({ id: z.string().meta({ privacy: privacy.nonPii }) })
              .meta({ privacy: privacy.structured }),
          )
          .nullish()
          .meta({ privacy: privacy.structured }),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    chitChats: z
      .object({
        // Chit Chats' OWN shipment id (e.g. "ABCDE12345"), assigned when we POST
        // the shipment at rating time. This is NOT the Redo `shipment_id`
        // (`redo-shp-…`) on the rate — that one loads our DB record; this one is
        // what every Chit Chats API call keys off: buy, refund, poll, and the
        // public tracking endpoint.
        chitChatsShipmentId: z.string().meta({ privacy: privacy.nonPii }),
        postageType: z.string().meta({ privacy: privacy.nonPii }),
        // The final-mile carrier's tracking number, populated once postage is
        // purchased. Kept for display; Redo's tracking lookups use
        // chitChatsShipmentId.
        carrierTrackingCode: z
          .string()
          .meta({ privacy: privacy.id.tracking })
          .nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    wegrow: z
      .object({
        serviceCode: z.string().meta({ privacy: privacy.nonPii }),
        carrierProduct: z.string().meta({ privacy: privacy.nonPii }),
        carrierName: z.string().meta({ privacy: privacy.nonPii }),
        shipmentId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
    canadaPost: z
      .object({
        manifestMode: createEnum(CanadaPostManifestMode, {
          enumName: "CanadaPostManifestMode",
        }).meta({ privacy: privacy.nonPii }),
        groupId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        manifestRequired: z
          .boolean()
          .meta({ privacy: privacy.nonPii })
          .nullish(),
        shipmentId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
        trackingPin: z
          .string()
          .meta({ privacy: privacy.id.tracking })
          .nullish(),
        supportsReturnSpec: z
          .boolean()
          .meta({ privacy: privacy.nonPii })
          .nullish(),
        returnLabelService: ServiceSchema.nullish().meta({
          privacy: privacy.structured,
        }),
        nativeReturnLabel: z
          .boolean()
          .meta({ privacy: privacy.nonPii })
          .nullish(),
      })
      .nullish()
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export const ProviderInfoSchema: z.ZodType<ProviderInfo> = ProviderInfoSchema_;
export interface ProviderInfo extends z.infer<typeof ProviderInfoSchema_> {}

// This is information necessary for Redo rate logic that is not provided by the shipping provider
// on a rate refresh. This information will not change if the shipment has not changed.
export const SupplementalRefreshInfoSchema = z
  .object({
    fedex_zone: z.number().meta({ privacy: privacy.nonPii }),
    usps_zone: z.number().meta({ privacy: privacy.nonPii }),
    saturday_delivery: z.boolean().meta({ privacy: privacy.nonPii }),
    is_merchant_carrier_account: z.boolean().meta({ privacy: privacy.nonPii }),
    is_parent_easypost_account: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    providerInfo: ProviderInfoSchema.nullish().meta({
      privacy: privacy.structured,
    }),
  })
  .meta({ privacy: privacy.structured });
export type SupplementalRefreshInfo = z.infer<
  typeof SupplementalRefreshInfoSchema
>;

// One parcel's share of a multi-parcel V2 rate. `shipmentId` is the persisted
// child outbound-shipment doc created at rating time; `provider_cost`/`rate`
// are USD and per-parcel (spread already applied to `rate`).
const parcelRateSchema = z
  .object({
    shipmentId: z.string().meta({ privacy: privacy.nonPii }),
    parcel: OutboundShipmentParcelV2Schema.meta({
      privacy: privacy.structured,
    }),
    provider_cost: z.string().meta({ privacy: privacy.nonPii }),
    rate: z.string().meta({ privacy: privacy.nonPii }),
    currency: z.string().meta({ privacy: privacy.nonPii }),
    providerInfo: ProviderInfoSchema.nullish().meta({
      privacy: privacy.structured,
    }),
  })
  .meta({ privacy: privacy.structured });
export type ParcelRate = z.infer<typeof parcelRateSchema>;

export const rawOutboundRateSchema = z
  .object({
    id: z.string().meta({ privacy: privacy.nonPii }),
    shipment_id: z.string().meta({ privacy: privacy.nonPii }),
    currency: z.string().meta({ privacy: privacy.nonPii }),
    carrier: CarrierSchema.meta({ privacy: privacy.nonPii }),
    service: ServiceSchema.meta({ privacy: privacy.structured }),
    delivery_date: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    delivery_days: z.number().meta({ privacy: privacy.nonPii }).nullish(),
    /**
     * Number of carrier-valid delivery days the package will spend in transit
     * (i.e. the carrier's SLA expressed as a count). Stable across quote days,
     * unlike `delivery_days`, which some providers compute from "now" so it
     * drifts when quoted on weekends. Use this for cross-provider comparison
     * and automation-driven selection. Falls back to `delivery_days` when not
     * populated.
     */
    transit_days: z.number().meta({ privacy: privacy.nonPii }).nullish(),
    delivery_date_guaranteed: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    created_at: z.string().meta({ privacy: privacy.nonPii }),
    updated_at: z.string().meta({ privacy: privacy.nonPii }),
    carrier_account_id: z.string().meta({ privacy: privacy.nonPii }),
    billing_type: z.string().meta({ privacy: privacy.nonPii }),
    provider_cost: z.string().meta({ privacy: privacy.nonPii }),
    rev_share_margin: z.string().meta({ privacy: privacy.nonPii }).nullish(), // This does not factor into the rate we or the merchant pays, but is used for competitive spread and reporting
    fedex_zone: z.number().meta({ privacy: privacy.nonPii }),
    usps_zone: z.number().meta({ privacy: privacy.nonPii }),
    ups_zone: z.number().meta({ privacy: privacy.nonPii }).nullish(),
    uniuni_zone: z.number().meta({ privacy: privacy.nonPii }).nullish(),
    mode: z.string().meta({ privacy: privacy.nonPii }),
    refund_status: createEnum(ShipmentRefundStatus, {
      enumName: "ShipmentRefundStatus",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    saturday_delivery: z.boolean().meta({ privacy: privacy.nonPii }),
    provider: createEnum(RateProvider, { enumName: "RateProvider" })
      .default(RateProvider.EASYPOST)
      .meta({ privacy: privacy.nonPii }),
    is_merchant_carrier_account: z.boolean().meta({ privacy: privacy.nonPii }),
    is_parent_easypost_account: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    payment_option: ShipmentPaymentOptionSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    providerInfo: ProviderInfoSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    /**
     * See the V2 outbound shipping provider for an explanation for the purpose of this field.
     */
    version: z.number().meta({ privacy: privacy.nonPii }).nullish(),
    /**
     * Per-parcel breakdown for a multi-parcel V2 rate: one entry per parcel,
     * carrying its persisted child shipment id, the parcel, and the parcel's own
     * provider cost and (per-parcel-spread-applied) rate, all converted to USD.
     * The top-level provider_cost/rate are the whole-shipment authoritative
     * numbers; these are the parts beneath them. Populated only when the shipment
     * has more than one parcel — single-parcel and V1 rates leave it null. Parcel
     * count is read via outboundRateParcelCount.
     */
    ratesByParcel: z
      .array(parcelRateSchema.meta({ privacy: privacy.structured }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    pricingProgram: createEnum(OutboundPricingProgram, {
      enumName: "OutboundPricingProgram",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });

export type RawOutboundRate = z.infer<typeof rawOutboundRateSchema>;

// This reporting enum only coincidentally matches shipping_claim_source. //redontIgnore
export enum CompSpreadType {
  MERCHANT = "merchant",
  REDO = "redo",
}

/**
 * Diagnostics for a competitive-spread adjustment. Sensitive — strip before
 * sending a rate to a merchant client.
 */
const competitiveSpreadMetadataSchema = z
  .object({
    compSpreadType: createEnum(CompSpreadType, {
      enumName: "CompSpreadType",
    }).meta({ privacy: privacy.nonPii }),
    /** Quoted price and spread this pass started from, not before any earlier pass. */
    originalRate: z.string().meta({ privacy: privacy.nonPii }),
    originalSpread: z.string().meta({ privacy: privacy.nonPii }),
    /** Spread as comp spread computes it: price minus cost net of rev share. */
    newSpread: z.string().meta({ privacy: privacy.nonPii }),
    /** Rate ids are per-quote, so carrier/service/price keep this readable later. */
    beat: z
      .object({
        rateId: z.string().meta({ privacy: privacy.nonPii }),
        shipmentId: z.string().meta({ privacy: privacy.nonPii }),
        carrier: CarrierSchema.meta({ privacy: privacy.nonPii }),
        service: ServiceSchema.meta({ privacy: privacy.structured }),
        rate: z.string().meta({ privacy: privacy.nonPii }),
        /** Falls back to a zero cost basis when the rate carries no provider cost. */
        spread: z.string().meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export type CompetitiveSpreadMetadata = z.infer<
  typeof competitiveSpreadMetadataSchema
>;

const dynamicPricingDataSchema = z
  .object({
    originalRate: z.string().meta({ privacy: privacy.nonPii }),
    originalSpread: z.string().meta({ privacy: privacy.nonPii }),
    newSpread: z.string().meta({ privacy: privacy.nonPii }),
    currency: createEnum(Currency, { enumName: "Currency" }).meta({
      privacy: privacy.nonPii,
    }),
    beat: z
      .object({
        rateId: z.string().meta({ privacy: privacy.nonPii }),
        shipmentId: z.string().meta({ privacy: privacy.nonPii }),
        carrier: CarrierSchema.meta({ privacy: privacy.nonPii }),
        service: ServiceSchema.meta({ privacy: privacy.structured }),
        rate: z.string().meta({ privacy: privacy.nonPii }),
        spread: z.string().nullable().meta({ privacy: privacy.nonPii }),
      })
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });
export type DynamicPricingData = z.infer<typeof dynamicPricingDataSchema>;

const RateSchema_ = rawOutboundRateSchema.extend({
  rate: z.string().meta({ privacy: privacy.nonPii }),
  competitive_spread_applied: z
    .boolean()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  redo_competitive_spread_applied: z
    .boolean()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  competitive_spread_data: competitiveSpreadMetadataSchema.nullish(),
  dynamicPricingData: dynamicPricingDataSchema.nullish(),
  rev_share_margin: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  automation_priority_rate: z
    .boolean()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
  provider_cost: z.string().meta({ privacy: privacy.nonPii }).nullish(), // Nullish for backwards compatibility
  fedex_zone: z.number().meta({ privacy: privacy.nonPii }).nullish(), // Nullish for backwards compatibility
  usps_zone: z.number().meta({ privacy: privacy.nonPii }).nullish(), // Nullish for backwards compatibility
  ups_zone: z.number().meta({ privacy: privacy.nonPii }).nullish(), // Nullish for backwards compatibility
  uniuni_zone: z.number().meta({ privacy: privacy.nonPii }).nullish(), // Nullish for backwards compatibility
  mode: z.string().meta({ privacy: privacy.nonPii }).nullish(), // Nullish for backwards compatibility
  is_merchant_carrier_account: z
    .boolean()
    .meta({ privacy: privacy.nonPii })
    .nullish(), // Nullish for backwards compatibility
  is_parent_easypost_account: z
    .boolean()
    .meta({ privacy: privacy.nonPii })
    .nullish(), // Nullish for backwards compatibility
  billing_type: z.string().meta({ privacy: privacy.nonPii }).nullish(), // Nullish for backwards compatibility
  saturday_delivery: z.boolean().meta({ privacy: privacy.nonPii }).nullish(), // Nullish for backwards compatibility
  landedCost: rateLandedCostSchema.nullish(),
  // Bought rate frozen in store currency at purchase, for frozen display.
  struck: StruckSnapshotSchema.nullish(),
  // Safe client projection populated before providerInfo is stripped.
  simple_rate_code: z.string().meta({ privacy: privacy.nonPii }).nullish(),
  aliasOfCarrierAccountId: z
    .string()
    .meta({ privacy: privacy.nonPii })
    .nullish(),
});

export const RateSchema = RateSchema_.extend({
  multiParcelRates: z
    .array(RateSchema_.meta({ privacy: privacy.structured }))
    .nullish()
    .meta({ privacy: privacy.structured }),
});

export type OutboundRate = z.infer<typeof RateSchema>;

/**
 * Number of labels a rate will produce. V1 multi-parcel rates carry one entry
 * per parcel in multiParcelRates; V2 multi-parcel rates carry one entry per
 * parcel in ratesByParcel. Falls back to 1 for single-parcel rates, which carry
 * neither field.
 */
export function outboundRateParcelCount(rate: OutboundRate): number {
  return rate.multiParcelRates?.length ?? rate.ratesByParcel?.length ?? 1;
}

export const CheckoutOutboundRateSchema = RateSchema.pick({
  provider_cost: true,
  rate: true,
  carrier: true,
  service: true,
  delivery_days: true,
  currency: true,
  shipment_id: true,
  carrier_account_id: true,
  pricingProgram: true,
}).extend({
  shipment_id: z.string().meta({ privacy: privacy.nonPii }).optional(),
  carrier_account_id: z.string().meta({ privacy: privacy.nonPii }).optional(),
  landedCostTotal: z.number().meta({ privacy: privacy.nonPii }).optional(),
  markedUpRate: z.string().meta({ privacy: privacy.nonPii }).optional(),
  landedCostBreakdown: z
    .object({
      duties: z.number().meta({ privacy: privacy.nonPii }),
      taxes: z.number().meta({ privacy: privacy.nonPii }),
      fees: z.number().meta({ privacy: privacy.nonPii }),
    })
    .optional()
    .meta({ privacy: privacy.structured }),
  landedCostBreakdownOriginal: z
    .object({
      duties: z.number().meta({ privacy: privacy.nonPii }),
      taxes: z.number().meta({ privacy: privacy.nonPii }),
      fees: z.number().meta({ privacy: privacy.nonPii }),
    })
    .optional()
    .meta({ privacy: privacy.structured }),
  breakdownDisplayMode: createEnum(BreakdownDisplayMode, {
    enumName: "BreakdownDisplayMode",
  })
    .optional()
    .meta({ privacy: privacy.nonPii }),
  breakdownLabelOverrides: z
    .object({
      shipping: z.string().meta({ privacy: privacy.nonPii }).optional(),
      taxes: z.string().meta({ privacy: privacy.nonPii }).optional(),
      dutiesAndFees: z.string().meta({ privacy: privacy.nonPii }).optional(),
    })
    .optional()
    .meta({ privacy: privacy.structured }),
});

export type CheckoutOutboundRate = z.infer<typeof CheckoutOutboundRateSchema>;

export const OutboundLabelShippingAddressSchema = z
  .object({
    name: z.string().meta({ privacy: privacy.person_name.customer }).nullish(),
    address1: z.string().meta({ privacy: privacy.address_line.customer }),
    address2: z
      .string()
      .meta({ privacy: privacy.address_line.customer })
      .nullish(),
    city: z.string().meta({ privacy: privacy.geo.coarse }),
    country: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
    country_code: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
    province: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
    province_code: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
    zip: z.string().meta({ privacy: privacy.geo.coarse }).nullish(),
    first_name: z
      .string()
      .meta({ privacy: privacy.person_name.customer })
      .nullish(),
    last_name: z
      .string()
      .meta({ privacy: privacy.person_name.customer })
      .nullish(),
    company: z
      .string()
      .meta({ privacy: privacy.company_name.customer })
      .nullish(),
    email: z.string().meta({ privacy: privacy.email.customer }).nullish(),
    phone: z.string().meta({ privacy: privacy.phone.customer }),
  })
  .meta({ privacy: privacy.structured });

/**
 * Canada Post shipment options. Delivery instructions (DNS, HFP, LAD) and proof
 * of age (PA18, PA19, PA21) have complex cross-conflicts per the Canada Post API,
 * so we model every valid combination as a single enum to make invalid states
 * unrepresentable. The only valid cross-group combo is PA18 + HFP.
 */
export enum CanadaPostOption {
  DO_NOT_SAFE_DROP = "DNS",
  CARD_FOR_PICKUP = "HFP",
  LEAVE_AT_DOOR = "LAD",
  PROOF_OF_AGE_18 = "PA18",
  PROOF_OF_AGE_19 = "PA19",
  PROOF_OF_AGE_21 = "PA21",
  PROOF_OF_AGE_18_CARD_FOR_PICKUP = "PA18+HFP",
}

export const CANADA_POST_OPTION_LABELS: Record<CanadaPostOption, string> = {
  [CanadaPostOption.DO_NOT_SAFE_DROP]: "Do not safe drop",
  [CanadaPostOption.CARD_FOR_PICKUP]: "Card for pickup",
  [CanadaPostOption.LEAVE_AT_DOOR]: "Leave at door",
  [CanadaPostOption.PROOF_OF_AGE_18]: "Proof of age (18+)",
  [CanadaPostOption.PROOF_OF_AGE_19]: "Proof of age (19+)",
  [CanadaPostOption.PROOF_OF_AGE_21]: "Proof of age (21+)",
  [CanadaPostOption.PROOF_OF_AGE_18_CARD_FOR_PICKUP]:
    "Proof of age (18+) with card for pickup",
};

export enum SpecialRateEligibility {
  MEDIA_MAIL = "USPS.MEDIAMAIL",
  LIBRARY_MAIL = "USPS.LIBRARYMAIL",
  MEDIA_MAIL_AND_LIBRARY_MAIL = "USPS.MEDIAMAIL,USPS.LIBRARYMAIL",
  DHL_BPM = "DHL.BPM",
  MEDIA_MAIL_AND_DHL_BPM = "USPS.MEDIAMAIL,DHL.BPM",
  LIBRARY_MAIL_AND_DHL_BPM = "USPS.LIBRARYMAIL,DHL.BPM",
  MEDIA_MAIL_AND_LIBRARY_MAIL_AND_DHL_BPM = "USPS.MEDIAMAIL,USPS.LIBRARYMAIL,DHL.BPM",
}

const SPECIAL_RATE_ELIGIBILITY_TOKENS = [
  SpecialRateEligibility.MEDIA_MAIL,
  SpecialRateEligibility.LIBRARY_MAIL,
  SpecialRateEligibility.DHL_BPM,
] as const;

export type SpecialRateEligibilityToken =
  (typeof SPECIAL_RATE_ELIGIBILITY_TOKENS)[number];

function specialRateEligibilityTokens(
  eligibility: SpecialRateEligibility | undefined | null,
): Set<string> {
  if (!eligibility) {
    return new Set();
  }
  return new Set(eligibility.split(","));
}

export function includesMediaMail(
  eligibility: SpecialRateEligibility | undefined | null,
): boolean {
  return specialRateEligibilityTokens(eligibility).has(
    SpecialRateEligibility.MEDIA_MAIL,
  );
}

export function includesLibraryMail(
  eligibility: SpecialRateEligibility | undefined | null,
): boolean {
  return specialRateEligibilityTokens(eligibility).has(
    SpecialRateEligibility.LIBRARY_MAIL,
  );
}

export function includesDhlBpm(
  eligibility: SpecialRateEligibility | undefined | null,
): boolean {
  return specialRateEligibilityTokens(eligibility).has(
    SpecialRateEligibility.DHL_BPM,
  );
}

export function withSpecialRateEligibility(
  current: SpecialRateEligibility | undefined | null,
  token: SpecialRateEligibilityToken,
  enabled: boolean,
): SpecialRateEligibility | null {
  const tokens = specialRateEligibilityTokens(current);
  if (enabled) {
    tokens.add(token);
  } else {
    tokens.delete(token);
  }
  const value = SPECIAL_RATE_ELIGIBILITY_TOKENS.filter((candidate) =>
    tokens.has(candidate),
  ).join(",");
  if (!value || !isEnumValue(value, SpecialRateEligibility)) {
    return null;
  }
  return value;
}

export function uspsSpecialRatesEligibility(
  eligibility: SpecialRateEligibility | undefined | null,
): SpecialRateEligibility | undefined {
  const value = SPECIAL_RATE_ELIGIBILITY_TOKENS.filter(
    (token) =>
      token !== SpecialRateEligibility.DHL_BPM &&
      specialRateEligibilityTokens(eligibility).has(token),
  ).join(",");
  if (!value || !isEnumValue(value, SpecialRateEligibility)) {
    return undefined;
  }
  return value;
}

const ShipmentRatesSchema_ = z
  .object({
    easypostShipmentId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    easypostOrderId: z.string().meta({ privacy: privacy.nonPii }).nullish(),
    // This is the parcel assigned to a purchased shipment rate
    parcel: OutboundParcelSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    // Since we treat multi-parcel shipment rates as a single "Rate", we use this to calculate ratesOutdated
    parcelsUsedToCalculateRate: z
      .array(OutboundParcelSchema.meta({ privacy: privacy.structured }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    // The custom_print_fields this rate was fetched with, for ratesOutdated
    customPrintFieldsUsedToCalculateRate: z
      .string()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    mode: z.string().meta({ privacy: privacy.nonPii }),
    rates: z
      .array(RateSchema.meta({ privacy: privacy.structured }))
      .meta({ privacy: privacy.structured }),
    /**  */
    weight: WeightSchema.meta({ privacy: privacy.structured }),
    originAddress: z
      .union([
        FulfillmentOrderAddressSchema.meta({ privacy: privacy.structured }),
        VerifiedAddressSchema.meta({ privacy: privacy.structured }),
      ])
      .nullish()
      .meta({ privacy: privacy.structured }),
    destinationAddress: z
      .union([
        FulfillmentOrderAddressSchema.meta({ privacy: privacy.structured }),
        VerifiedAddressSchema.meta({ privacy: privacy.structured }),
      ])
      .nullish()
      .meta({ privacy: privacy.structured }),
    signatureRequired: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    adultSignatureRequired: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    specialRateEligibility: createEnum(SpecialRateEligibility, {
      enumName: "SpecialRateEligibility",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    directDeliveryOnly: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    saturdayDelivery: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    carbonNeutral: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    canadaPostOption: createEnum(CanadaPostOption, {
      enumName: "CanadaPostOption",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    nonDeliveryOption: createEnum(NonDeliveryOption, {
      enumName: "NonDeliveryOption",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    ddp: z.boolean().meta({ privacy: privacy.nonPii }).nullish(),
    returnAddress: FulfillmentOrderAddressSchema.nullish().meta({
      privacy: privacy.structured,
    }),
    shipmentIds: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    shipmentMessages: z
      .record(
        z.string().meta({ privacy: privacy.nonPii }),
        z
          .array(ShipmentMessageSchema.meta({ privacy: privacy.structured }))
          .meta({ privacy: privacy.structured }),
      )
      .nullish()
      .meta({ privacy: privacy.untypedPayload }),
    createdAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
    itn: z.string().meta({ privacy: privacy.id.government }).nullish(),
    /** @deprecated */
    dryIceWeight: WeightSchema.nullish().meta({ privacy: privacy.structured }),
    /** @deprecated */
    hazmat: HazmatSchema.nullish().meta({ privacy: privacy.nonPii }),
    taxIdentifierIds: z
      .array(z.string().meta({ privacy: privacy.nonPii }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    customerTaxIds: z
      .array(OutboundShipmentTaxIdSchema.meta({ privacy: privacy.structured }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    uspsExemptionCode: createEnum(USPSExemptionCode, {
      enumName: "USPSExemptionCode",
    })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    additionalCustomsComments: z
      .string()
      .meta({ privacy: privacy.text.merchant })
      .nullish(),
    contentsExplanation: z
      .string()
      .meta({ privacy: privacy.text.merchant })
      .nullish(),
    contentsType: createEnum(ContentsType, { enumName: "ContentsType" })
      .nullish()
      .meta({ privacy: privacy.nonPii }),
    customsDeclarations: z
      .array(CustomsDeclarationSchema.meta({ privacy: privacy.structured }))
      .nullish()
      .meta({ privacy: privacy.structured }),
    refreshRatesAt: DateFromDateOrIsoStringSchema.meta({
      privacy: privacy.nonPii,
    }).nullish(),
  })
  .meta({ privacy: privacy.structured });

export const ShipmentRatesSchema: z.ZodType<ShipmentRates> =
  ShipmentRatesSchema_;

export interface ShipmentRates extends z.infer<typeof ShipmentRatesSchema_> {}

// If the rate came from a child account (aka it's a merchant rate) but easypost still tells us that it's billed through easypost, we need to charge for the label
/** Billing-relevant subset of a rate, so callers holding only these fields
 * (e.g. passed over the wire) can decide chargeability without a full rate. */
export type ShouldChargeForLabelFields = Pick<
  OutboundRate,
  | "provider"
  | "is_parent_easypost_account"
  | "payment_option"
  | "is_merchant_carrier_account"
  | "billing_type"
>;

/** The fields the provider factory actually reads to route to a provider, so
 * a rate rebuilt from a doc snapshot (void) needs no filler values. */
export type RoutableOutboundRate = Pick<
  OutboundRate,
  | "version"
  | "provider"
  | "carrier"
  | "carrier_account_id"
  | "billing_type"
  | "is_merchant_carrier_account"
  | "is_parent_easypost_account"
  | "providerInfo"
>;

export function shouldChargeForLabel(rate: ShouldChargeForLabelFields) {
  // Even though we treat all providers the same right now, we want to force developers to consider about this logic when they add a new provider
  // Addint this check ensures we think about this logic when we add a new provider
  switch (rate.provider) {
    case RateProvider.EASYPOST:
    case RateProvider.UPS:
    case RateProvider.LANDMARK_GLOBAL:
    case RateProvider.REDO_LANDMARK_GLOBAL:
    case RateProvider.INTERNATIONAL_BRIDGE:
    case RateProvider.ETA_EXPRESS:
    case RateProvider.SOAP_BOX:
    case RateProvider.FEDEX:
    case RateProvider.TEST_CARRIER:
    case RateProvider.DHL_ECOMMERCE:
    case RateProvider.UNIUNI:
    case RateProvider.CANADA_POST:
    case RateProvider.GLOBALE:
      // If a merchant has brought their own parent easypost account, all of the billing is handled outside of redo
      if (rate.is_parent_easypost_account) {
        return false;
      }

      if (isExternalAccountBilling(rate.payment_option)) {
        return false;
      }

      return (
        !rate.is_merchant_carrier_account || rate.billing_type === "easypost"
      );
    case RateProvider.USPS:
      // USPS doesn't support external account billing, but adding this just in case for future
      if (isExternalAccountBilling(rate.payment_option)) {
        return false;
      }

      return rate.billing_type === UspsBillingType.REDO;
    case RateProvider.TIKTOK:
      return false; // We never pay for these labels, so we should never charge for them
    case RateProvider.SENDCLOUD:
      // Sendcloud is returns-only and only routes through merchant accounts;
      // outbound code paths should never reach this branch for Sendcloud rates.
      return false;
    case RateProvider.STALLION_EXPRESS:
      // Stallion Express is BYOA only — merchant pays the carrier directly.
      return false;
    case RateProvider.ECOM_EXPRESS:
      // Ecom Express is BYOA only — merchant pays the carrier directly.
      return false;
    case RateProvider.AMAZON_BUY_SHIPPING:
      // Amazon Buy Shipping charges the connected Amazon merchant account.
      return false;
    case RateProvider.AMAZON_SHIPPING:
      return !rate.is_merchant_carrier_account;
    case RateProvider.CHITCHATS:
      // Chit Chats is BYOA only — merchant pays the carrier directly.
      return false;
    case RateProvider.FREIGHTWIRE:
      // Post-paid and billed to the merchant; never lands on Redo's bill.
      return false;
    case RateProvider.WEGROW:
      // WeGrow invoices Redo for the Netherlands warehouse's labels.
      return true;
    default:
      assertNever(rate.provider);
  }
}

export function isMultiParcelShipmentRates(
  shipmentRates: ShipmentRates,
): boolean {
  return (
    !!shipmentRates.parcelsUsedToCalculateRate &&
    shipmentRates.parcelsUsedToCalculateRate.length > 1
  );
}

export function getShipmentParcels(shipmentRates: ShipmentRates) {
  if (isMultiParcelShipmentRates(shipmentRates)) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return shipmentRates.parcelsUsedToCalculateRate!;
  }
  return shipmentRates.parcel ? [shipmentRates.parcel] : [];
}

export function isPoBoxEnabledRate(
  rate: OutboundRate,
  country?: string | null,
): boolean {
  // FedEx Ground Economy (Smart Post) is a hybrid service that uses USPS for final-mile delivery,
  // so it can deliver to PO Boxes
  if (
    isFedexCarrier(rate.carrier) &&
    rate.service === FedexServices.SMART_POST
  ) {
    return true;
  }
  if (PO_BOX_ENABLED_CARRIERS.includes(rate.carrier)) {
    return true;
  }
  const normalizedCountry = country?.toUpperCase();
  if (normalizedCountry === "CA" && rate.carrier === Carriers.CANADA_POST) {
    return true;
  }
  if (
    (normalizedCountry === "AU" || normalizedCountry === "US") &&
    rate.carrier === Carriers.AUSTRALIA_POST
  ) {
    return true;
  }
  return false;
}

export function isAPOEnabledRate(rate: OutboundRate) {
  // Out UPS integration is not ready to handle APO addresses, they need us to make changes to the integration
  return (
    APO_ENABLED_CARRIERS.includes(rate.carrier) &&
    rate.provider !== RateProvider.UPS
  );
}

export const CARRIER_RATE_FETCH_TIMED_OUT_MESSAGE =
  "Carrier rate fetch timed out";

export const RETRYABLE_ERROR_MESSAGES = [
  /** Our own carrier deadline fired before the carrier answered — seen with multi-parcel shipments */
  CARRIER_RATE_FETCH_TIMED_OUT_MESSAGE,
  /** Easypost returns this rates message for any carrier that does not provide rates within 10 secs. We have seen this mostly with FirstMile, but also occasionally DHL, APC */
  "Carrier did not respond within 10 seconds.",
  /** Received on Amazon rates due to being rate limited by Amazon (not an EasyPost rate limit) */
  "You exceeded your quota for the requested resource.",
  /** EasyPost sometimes disables FM for a few minutes because they detect performance issues */
  "Rating with FirstMileConciseAccount (production mode) is temporarily disabled due to carrier performance issues",
  /** Returned when direct UPS rates are unavailable due to 503 responses */
  "UPS rates are temporarily unavailable",
  /** Returned by Easypost when they can't contact the carrier. Seen with UPS so far */
  "The carrier failed to respond, please try again.",
  /** Returned by Amazon when there are insufficient funds in our account */
  "Unable to print due to insufficient credit balance.",
  /** Transient error when retrieving Amazon rates */
  "Unable to retrieve AmazonShipping rates. Please try again or contact support",
  /** Returned by Easypost when they can't contact the carrier. Seen with Ontrac so far */
  "Rating with OntracV3Account (production mode) is temporarily disabled due to carrier performance issues",
  /** Transient error returned by several direct integrations */
  "Connection aborted",
  /** Transient carrier response that could not be parsed — safe to retry */
  "The carrier returned a response we were not able to process. Please try again later.",
  /** EasyPost returns this when they detect performance issues with the carrier */
  "is temporarily disabled due to carrier performance issues",
  /** Transient request timeout — seen with DHL eCommerce when the carrier is slow to respond */
  "timeout of 8000ms exceeded",
  /** Transient Canada Post Developer Portal gateway failures. Permanent 4xx (e.g. AA002) is excluded so it can't retry forever. */
  "Canada Post get rates failed with HTTP 408",
  "Canada Post get rates failed with HTTP 429",
  "Canada Post get rates failed with HTTP 500",
  "Canada Post get rates failed with HTTP 502",
  "Canada Post get rates failed with HTTP 503",
  "Canada Post get rates failed with HTTP 504",
  "Canada Post get rates transport failed",
] as const;

export const outboundLabelErrorSchema = z
  .object({
    message: z.string().meta({ privacy: privacy.systemErrorText }),
    retryable: z.boolean().meta({ privacy: privacy.nonPii }),
  })
  .meta({ privacy: privacy.structured });

export type OutboundLabelError = z.infer<typeof outboundLabelErrorSchema>;

export const carrierShipmentSchema = z
  .object({
    carrier: CarrierSchema.meta({ privacy: privacy.nonPii }),
    service: ServiceSchema.meta({ privacy: privacy.structured }),
    redoShipmentId: z.string().meta({ privacy: privacy.nonPii }),
    rate: MoneySchema.meta({ privacy: privacy.structured }),
    providerCost: MoneySchema.meta({ privacy: privacy.structured }),
    commercialInvoiceUrl: z
      .string()
      .meta({ privacy: privacy.privateUrl })
      .nullish(),
    comInvoiceSubmittedElectronically: z
      .boolean()
      .meta({ privacy: privacy.nonPii })
      .nullish(),
    shipmentPackages: z
      .array(
        z
          .object({
            trackingNumber: z.string().meta({ privacy: privacy.id.tracking }),
            labelUrl: z.string().meta({ privacy: privacy.privateUrl }),
            trackingUrl: z
              .string()
              .meta({ privacy: privacy.publicUrl })
              .nullish(),
            rate: MoneySchema.meta({ privacy: privacy.structured }),
            providerCost: MoneySchema.meta({ privacy: privacy.structured }),
          })
          .meta({ privacy: privacy.structured }),
      )
      .meta({ privacy: privacy.structured }),
  })
  .meta({ privacy: privacy.structured });

export type CarrierShipment = z.infer<typeof carrierShipmentSchema>;
